'use client'

import { useState } from 'react'
import {
  buildAfterLoginHref,
  buildTwoFactorPageHref,
  resolveSafeNextPath,
} from '@/lib/utils/safe-next-path'
import { useSearchParams, useRouter } from 'next/navigation'
import { useAuthStore } from '../../../../shared/platform/store'
import { authClient, isValidUserProfile } from '../../../../shared/platform/lib'
import { toast } from 'sonner'
import { setSentryUser } from '@/lib/sentry/context'
import {
  INVALID_CREDENTIALS_MESSAGE,
  NO_TWO_FACTOR_METHOD_MESSAGE,
  resolveSignInOutcome,
} from './sign-in-outcome'
import { getSkewProtectionHeaders } from '@/lib/auth/skew-protection-headers'

export interface LoginFormState {
  isLoading: boolean
  email: string
  password: string
  showPassword: boolean
  loginError: string
  /** ログイン後の行き先（安全な同一サイト相対パス）。Google 認証にも同じ行き先を渡す */
  nextPath: string
}

// 実体は lib/utils/safe-next-path.ts（画面部品からも副作用なしで使えるよう切り出した）。既存の import 元は変えない
export { resolveSafeNextPath }

export interface LoginFormActions {
  setEmail: (v: string) => void
  setPassword: (v: string) => void
  setShowPassword: (v: boolean) => void
  setLoginError: (v: string) => void
  handleEmailLogin: (e: React.FormEvent) => void
}

export function useLoginForm(): LoginFormState & LoginFormActions {
  const { setUser } = useAuthStore()
  const router = useRouter()
  const searchParams = useSearchParams()
  const nextPath = resolveSafeNextPath(searchParams.get('next'))

  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState('')

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setLoginError('')

    try {
      // better-auth でメール・パスワード認証（リトライ付き）
      // 429（レート制限超過）時、better-auth は残り待ち秒数を X-Retry-After ヘッダーで返す
      // (node_modules/better-auth/dist/api/rate-limiter/index.mjs の rateLimitResponse)。
      // onError フックでレスポンスヘッダーを読み、具体的な待ち時間をユーザーに提示する。
      let retryAfterSeconds: number | null = null
      const attemptSignIn = () =>
        authClient.signIn.email(
          { email, password },
          {
            onError: (ctx) => {
              const header = ctx.response.headers.get('X-Retry-After')
              const parsed = header ? Number(header) : NaN
              if (Number.isFinite(parsed) && parsed > 0) {
                retryAfterSeconds = parsed
              }
            },
          }
        )

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('認証がタイムアウトしました')), 30000)
      })

      let lastError: unknown = null
      let data: Awaited<ReturnType<typeof authClient.signIn.email>>['data'] = null
      let error: Awaited<ReturnType<typeof authClient.signIn.email>>['error'] = null

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const result = await Promise.race([attemptSignIn(), timeoutPromise])
          data = result.data
          error = result.error
          // better-auth からの確定レスポンス（成功・401・429 等）はリトライしても結果が変わらない。
          // ここで再送すると IP 単位のレート制限（15分30回・lib/auth/auth.ts）を
          // 1クリックで2回消費してしまいロックを早めるため、確定応答時は即break。
          // リトライ対象は Promise.race のタイムアウト・ネットワーク例外（catch節）のみに限定する。
          break
        } catch (e) {
          lastError = e
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 2000))
          }
        }
      }

      // 401 / 403 / 429 / 2FA / 成功 を取り違えないよう、意味づけは純粋関数へ寄せる
      // （分岐の根拠は ./sign-in-outcome.ts の JSDoc）。
      const outcome = resolveSignInOutcome(
        data as Parameters<typeof resolveSignInOutcome>[0],
        error as Parameters<typeof resolveSignInOutcome>[1]
      )

      if (outcome.kind === 'rate-limited') {
        const waitText = retryAfterSeconds
          ? `約${Math.max(1, Math.ceil(retryAfterSeconds / 60))}分後に`
          : 'しばらく時間をおいてから'
        setLoginError(`ログイン試行回数が上限に達しました。${waitText}再度お試しください。`)
        return
      }

      if (outcome.kind === 'blocked') {
        // サーバ（SIGN_IN_BLOCK_MESSAGES）が利用者向けに書いた文言。そのまま出す。
        setLoginError(outcome.message)
        return
      }

      if (outcome.kind === 'invalid-credentials') {
        setLoginError(INVALID_CREDENTIALS_MESSAGE)
        return
      }

      if (lastError && !data) {
        throw lastError
      }

      if (outcome.kind === 'two-factor') {
        if (!outcome.hasMethod) {
          setLoginError(NO_TWO_FACTOR_METHOD_MESSAGE)
          return
        }
        // auth-client の onTwoFactorRedirect と同じ行き先（ログイン画面の next を引き継ぐ）
        router.push(buildTwoFactorPageHref(`?${searchParams.toString()}`))
        return
      }

      if (outcome.kind === 'authenticated') {
        // better-auth 認証成功 - fetch user profile via API (bypasses RLS)
        try {
          const profileController = new AbortController()
          const profileTimeout = setTimeout(() => profileController.abort(), 15000)
          const profileResponse = await fetch(
            `/api/auth/profile?email=${encodeURIComponent(outcome.email)}`,
            {
              signal: profileController.signal,
              headers: getSkewProtectionHeaders(),
            }
          ).finally(() => clearTimeout(profileTimeout))

          if (profileResponse.ok) {
            const profile = await profileResponse.json()
            if (isValidUserProfile(profile)) {
              setUser(profile)
              setSentryUser(profile)
              toast.success(`${profile.name}としてログインしました`)
              // 2026-05-17 西野氏方針 (PR #1720):
              //   強制パスワード変更画面 (/change-password) は廃止。
              //   パスワード変更はプロフィールモーダルから自由に行う。
              //   旧実装の `profile.mustChangePassword ? '/change-password' : nextPath`
              //   分岐は削除して常に nextPath へ遷移する (Codex SO 指摘・HIGH 2 完全対応)。
              // AUTH-01（2026-10-30）: 戻り先へ直接は行かず、行き先を決める口を通す
              //   （2段階認証が必須の人は業務の画面を通らずに登録の画面へ送られる）。
              window.location.href = buildAfterLoginHref(nextPath)
              return
            }
          }

          setLoginError('ユーザー情報の取得に失敗しました。管理者にお問い合わせください。')
          return
        } catch {
          setLoginError('ユーザー情報の取得に失敗しました。ネットワーク接続を確認してください。')
          return
        }
      }

      setLoginError('ログインに失敗しました。もう一度お試しください。')
    } catch (err) {
      if (err instanceof Error && err.message === '認証がタイムアウトしました') {
        setLoginError('認証がタイムアウトしました。ネットワーク接続を確認してください。')
      } else {
        setLoginError('ログインに失敗しました')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    email,
    password,
    showPassword,
    loginError,
    nextPath,
    setEmail,
    setPassword,
    setShowPassword,
    setLoginError,
    handleEmailLogin,
  }
}
